import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Loader2, Sparkles, X, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { garmentHex, designThemeFor, rexvenInk, type CustomMockup, type MockupAnalysis, type StudyTone } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { PRESENTATION_LABEL, PRODUCT_TYPE_LABEL } from "../../lib/customListing";
import type { useSupplierVariations } from "../../lib/useSupplierVariations";

// Giyim mockup analizi: urun tipi, sunum, fotograftaki urunler (renk/ton/yuz), tema,
// marka/model/renk (Rexven katalogundan). Form hem "Mockup sablonunu kur" penceresinin
// icinde (alanlarla birlikte kaydedilir) hem de karttan acilan pencerede kullanilir.

type Item = MockupAnalysis["items"][number];
type Vars = ReturnType<typeof useSupplierVariations>;
const TONES: StudyTone[] = ["light", "mid", "dark"];
const TONE_LABEL: Record<StudyTone, string> = { light: "Açık", mid: "Orta", dark: "Koyu" };
const inputCls = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";
/** Urun satiri hucresi: genisligi satir duzeni verir (w-full ile sabit genislik cakisip tasiyordu) */
const cellCls = "rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";

/** Varsayilan markalar; katalogdakiler ve mockuplarda kullanilanlar eklenir, yenisi elle girilir */
export const DEFAULT_BRANDS = ["Comfort Colors", "Gildan", "Bella Canvas"];

const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, "");
const hexDist = (a: string, b: string) => {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const x = p(a), y = p(b);
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
};
/** AI'in renk adini (ya da hex'ini) katalogdaki en yakin renge esler: ad esitligi,
 *  ad icerme, sonra renk uzakligi. Liste bossa ad aynen kalir. */
export function matchCatalogColor(name: string, hex: string | undefined, catalog: string[]): string {
  if (!catalog.length || !name) return name;
  const n = norm(name);
  const exact = catalog.find((c) => norm(c) === n);
  if (exact) return exact;
  const part = catalog.find((c) => norm(c).includes(n) || n.includes(norm(c)));
  if (part) return part;
  const target = hex ?? garmentHex(name);
  return [...catalog].sort((a, b) => hexDist(garmentHex(a), target) - hexDist(garmentHex(b), target))[0];
}

/** Alan temasi = basilacak TASARIMIN rengi (2026-10-04 kurali): acik/orta tisorte koyu
 *  tasarim ("dark"), koyu tisorte acik tasarim ("light") */
export const themeForTone = (tone: StudyTone): "light" | "dark" => (tone === "dark" ? "light" : "dark");
/** Urunun basilacak tasarimi: Rexven baski rengi onerisi once, yoksa kumas tonu */
export const themeForItem = (it: { tone: StudyTone; color?: string | null }, brandModel?: string | null) => designThemeFor(it.tone, it.color, brandModel);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/mockups${path}`, { method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error?.message ?? j.error ?? `İstek başarısız (${r.status})`);
  return j.data as T;
}

/** Analiz formunun durumu + katalog eslemesi + kaydetme */
export function useAnalysisDraft(mockup: CustomMockup, vars?: Vars, brandOptions?: string[]) {
  const a = mockup.mockupAnalysis ?? null;
  const [productType, setProductType] = useState<MockupAnalysis["productType"]>(a?.productType ?? "tshirt");
  const [presentation, setPresentation] = useState<MockupAnalysis["presentation"]>(a?.presentation ?? "model");
  const [items, setItems] = useState<Item[]>(a?.items ?? []);
  const [tags, setTags] = useState((a?.tags ?? []).join(", "));
  const [noteTr, setNoteTr] = useState(a?.noteTr ?? "");
  const [brand, setBrand] = useState(mockup.brand ?? "");
  const [model, setModel] = useState(mockup.model ?? "");
  const [color, setColor] = useState(mockup.color ?? "");
  const [approved, setApproved] = useState(a?.status === "approved");
  const [productTypeFrom, setProductTypeFrom] = useState(a?.productTypeFrom);
  const [touched, setTouched] = useState(false);
  const brands = [...new Set([...DEFAULT_BRANDS, ...(vars?.brands ?? []), ...(brandOptions ?? []), ...(brand ? [brand] : [])])].sort();
  // Katalog: secili markanin modelleri, marka+modelin stoktaki renkleri (marka katalogda yoksa tum renkler)
  const catalogBrand = vars?.brands.includes(brand) ? brand : "";
  const models = vars?.hasCatalog ? vars.modelsForBrand(catalogBrand) : [];
  const catalogColors = vars?.hasCatalog ? vars.colorsFor(catalogBrand, models.includes(model) ? model : "") : [];
  const inCatalog = (c: string) => catalogColors.some((x) => norm(x) === norm(c));

  /** AI sonucunu forma doldurur: renk adlari Rexven renklerine eslenir */
  const fill = (x: MockupAnalysis) => {
    setProductType(x.productType); setPresentation(x.presentation); setTags(x.tags.join(", ")); setNoteTr(x.noteTr ?? "");
    setProductTypeFrom(x.productTypeFrom);
    const mapped = x.items.map((it) => ({ ...it, color: matchCatalogColor(it.color, it.hex, catalogColors) }));
    setItems(mapped);
    if (!color && mapped.length === 1) setColor(mapped[0].color);
    setTouched(true);
  };
  // Katalog gelince eski AI adlari da eslenir (kaydedince yazilir)
  useEffect(() => {
    if (catalogColors.length) setItems((xs) => xs.map((it) => (inCatalog(it.color) ? it : { ...it, color: matchCatalogColor(it.color, it.hex ?? garmentHex(it.color), catalogColors) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogColors.join("|")]);

  const has = touched || !!a;
  const save = (status?: "draft" | "approved") => call<CustomMockup>("PATCH", `/${mockup.id}/analysis`, {
    productType, presentation, items, tags: tags.split(",").map((t) => t.trim()).filter(Boolean), noteTr,
    status: status ?? (approved ? "approved" : "draft"), brand, model,
    // Ayri "mockup rengi" alani yok: tek urunlu mockupta urunun rengi, coklu urunde eski deger kalir
    color: items.length === 1 ? items[0].color : color,
  });
  const mark = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setTouched(true); };
  return {
    a, has, touched, productType, setProductType: mark(setProductType), presentation, setPresentation: mark(setPresentation),
    items, setItems: mark(setItems), tags, setTags: mark(setTags), noteTr, setNoteTr: mark(setNoteTr),
    brand, setBrand: mark(setBrand), model, setModel: mark(setModel), color, setColor: mark(setColor),
    approved, setApproved: mark(setApproved), productTypeFrom, brands, models, catalogColors, inCatalog, vars, fill, save,
  };
}
export type AnalysisDraft = ReturnType<typeof useAnalysisDraft>;

/** Form alanlari. compact: dar panel (sablon penceresinin sag tarafi) */
export function MockupAnalysisFields({ d, areaCount, compact }: { d: AnalysisDraft; areaCount: number; compact?: boolean }) {
  const [newBrand, setNewBrand] = useState(false);
  const setItem = (i: number, patch: Partial<Item>) => d.setItems(d.items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const sm = compact ? "py-1.5 text-xs" : "";
  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className={clsx("grid gap-3", compact ? "grid-cols-2" : "sm:grid-cols-2")}>
        <div>
          <label htmlFor="ma-type" className="mb-1 block text-xs font-medium text-gray-700">
            Ürün tipi{d.productTypeFrom === "listing" ? <span className="ml-1 font-normal text-gray-500" title="Ürün tipi ilham listesinden alındı">(ilhamdan)</span> : null}
          </label>
          <select id="ma-type" value={d.productType} onChange={(e) => d.setProductType(e.target.value as MockupAnalysis["productType"])} className={clsx(inputCls, sm)}>
            {Object.entries(PRODUCT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ma-pres" className="mb-1 block text-xs font-medium text-gray-700">Sunum</label>
          <select id="ma-pres" value={d.presentation} onChange={(e) => d.setPresentation(e.target.value as MockupAnalysis["presentation"])} className={clsx(inputCls, sm)}>
            {Object.entries(PRESENTATION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className={clsx("grid gap-3", compact ? "grid-cols-1" : "sm:grid-cols-2")}>
        <div>
          <label htmlFor="ma-brand" className="mb-1 block text-xs font-medium text-gray-700">Marka</label>
          {newBrand ? (
            <input id="ma-brand" autoFocus value={d.brand} onChange={(e) => d.setBrand(e.target.value)} onBlur={() => setNewBrand(false)} placeholder="Yeni marka adı" className={clsx(inputCls, sm)} />
          ) : (
            <select id="ma-brand" value={d.brand} onChange={(e) => { if (e.target.value === "__new__") { d.setBrand(""); setNewBrand(true); } else { d.setBrand(e.target.value); d.setModel(""); } }} className={clsx(inputCls, sm)}>
              <option value="">Seç…</option>
              {d.brands.map((b) => <option key={b} value={b}>{b}</option>)}
              <option value="__new__">+ Yeni marka ekle</option>
            </select>
          )}
        </div>
        <div>
          <label htmlFor="ma-model" className="mb-1 block text-xs font-medium text-gray-700">Model (Rexven)</label>
          {d.models.length ? (
            <select id="ma-model" value={d.models.includes(d.model) ? d.model : ""} onChange={(e) => d.setModel(e.target.value)} className={clsx(inputCls, sm)}>
              <option value="">{d.model && !d.models.includes(d.model) ? `${d.model} (katalogda yok)` : "Seç…"}</option>
              {d.models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <input id="ma-model" value={d.model} onChange={(e) => d.setModel(e.target.value)} placeholder="1717" className={clsx(inputCls, sm)} />
          )}
        </div>
      </div>
      <p className="-mt-1 text-xs text-gray-500">Marka ve model seçince ürün renkleri o modelin Rexven'de stokta olan renklerinden seçilir.</p>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Fotoğraftaki ürünler (okuma sırasıyla)</span>
          <button onClick={() => d.setItems([...d.items, { color: d.catalogColors[0] ?? "White", tone: "light", side: "front" }])}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50"><Plus className="h-3.5 w-3.5" /> Ürün ekle</button>
        </div>
        <p className="mb-2 text-xs text-gray-500">Basılacak tasarımı önce Rexven'in baskı rengi önerisi belirler (renk adı tablodaysa, rozette "Rexven" yazar); yoksa kumaş tonu: açık ve orta tonlu tişörte koyu tasarım, koyu tişörte açık tasarım.</p>
        {d.items.length === 0 ? <p className="text-xs text-gray-500">Ürün yok. AI ile otomatik kur ya da elle ekle.</p> : (
          <div className="space-y-2">
            {!compact && (
              <div className="grid grid-cols-[1.25rem_1fr_7rem_6rem_6.5rem_2rem] gap-2 px-0.5 text-xs font-medium text-gray-500">
                <span>#</span><span>Renk (Rexven)</span><span>Kumaş tonu</span><span>Yüz</span><span>Basılacak tasarım</span><span />
              </div>
            )}
            {d.items.map((it, i) => {
              const out = !!d.catalogColors.length && !d.inCatalog(it.color);
              const colorCtl = d.catalogColors.length ? (
                <select aria-label={`${i + 1}. ürün rengi`} value={d.inCatalog(it.color) ? d.catalogColors.find((c) => norm(c) === norm(it.color)) : ""} onChange={(e) => setItem(i, { color: e.target.value })}
                  className={clsx(cellCls, "w-full", out && "border-amber-300")}>
                  <option value="">{it.color && out ? `${it.color} (katalogda yok)` : "Seç…"}</option>
                  {d.catalogColors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input aria-label={`${i + 1}. ürün rengi`} value={it.color} onChange={(e) => setItem(i, { color: e.target.value })} className={clsx(cellCls, "w-full")} />
              );
              const toneCtl = (
                <select aria-label={`${i + 1}. ürün kumaş tonu`} value={it.tone} onChange={(e) => setItem(i, { tone: e.target.value as StudyTone })} className={clsx(cellCls, "w-full")}>
                  {TONES.map((t) => <option key={t} value={t}>{TONE_LABEL[t]}</option>)}
                </select>
              );
              const sideCtl = (
                <select aria-label={`${i + 1}. ürün yüzü`} value={it.side} onChange={(e) => setItem(i, { side: e.target.value as Item["side"] })} className={clsx(cellCls, "w-full")}>
                  <option value="front">Ön</option><option value="back">Arka</option>
                </select>
              );
              const bm = `${d.brand} ${d.model}`;
              const ink = rexvenInk(it.color, bm);
              const lightDesign = themeForItem(it, bm) === "light";
              const designBadge = (
                <span title={ink === "white" ? "Rexven önerisi: beyaz baskı" : ink === "black" ? "Rexven önerisi: siyah baskı" : ink === "both" ? "Rexven: beyaz ya da siyah olur; kumaş tonuna göre seçildi" : "Kumaş tonuna göre"}
                  className={clsx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", lightDesign ? "bg-gray-100 text-gray-700" : "bg-gray-800 text-white")}>
                  {lightDesign ? "Açık tasarım" : "Koyu tasarım"}{ink && ink !== "both" ? " · Rexven" : ""}
                </span>
              );
              const del = (
                <button onClick={() => d.setItems(d.items.filter((_, j) => j !== i))} aria-label={`${i + 1}. ürünü sil`} className="rounded-lg p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              );
              const swatch = <span className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300" style={{ background: garmentHex(it.color) }} />;
              return compact ? (
                <div key={i} className="space-y-1.5 rounded-lg border border-gray-100 p-2">
                  <div className="flex items-center gap-2">
                    <span className="w-4 flex-shrink-0 text-xs text-gray-400">{i + 1}</span>
                    {swatch}
                    <div className="min-w-0 flex-1">{colorCtl}</div>
                    {del}
                  </div>
                  <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 pl-6">
                    <label className="text-xs text-gray-500">Kumaş tonu{toneCtl}</label>
                    <label className="text-xs text-gray-500">Yüz{sideCtl}</label>
                    <div className="self-end pb-1">{designBadge}</div>
                  </div>
                </div>
              ) : (
                <div key={i} className="grid grid-cols-[1.25rem_1fr_7rem_6rem_6.5rem_2rem] items-center gap-2">
                  <span className="text-xs text-gray-400">{i + 1}</span>
                  <div className="flex min-w-0 items-center gap-2">{swatch}<div className="min-w-0 flex-1">{colorCtl}</div></div>
                  {toneCtl}{sideCtl}{designBadge}{del}
                </div>
              );
            })}
          </div>
        )}
        {d.items.some((it) => !!d.catalogColors.length && !d.inCatalog(it.color)) && (
          <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> Sarı çerçeveli ürünlerin rengi Rexven kataloğunda (stokta) yok. Katalogdaki bir renge çevir; yoksa bu mockup üretilemeyen bir renkle listeye girebilir.
          </p>
        )}
        {areaCount > 0 && d.items.length > 0 && areaCount !== d.items.length && (
          <p className="mt-1 text-xs text-amber-700">Fotoğrafta {d.items.length} ürün var ama {areaCount} baskı alanı tanımlı.</p>
        )}
      </div>

      <div>
        <label htmlFor="ma-tags" className="mb-1 block text-xs font-medium text-gray-700">Tema etiketleri (virgülle)</label>
        <input id="ma-tags" value={d.tags} onChange={(e) => d.setTags(e.target.value)} placeholder="christmas, fall" className={clsx(inputCls, sm)} />
      </div>
      <div>
        <label htmlFor="ma-note" className="mb-1 block text-xs font-medium text-gray-700">Not</label>
        <input id="ma-note" value={d.noteTr} onChange={(e) => d.setNoteTr(e.target.value)} maxLength={200} className={clsx(inputCls, sm)} />
      </div>
    </div>
  );
}

/** Karttan acilan pencere (sablon penceresi acmadan analizi duzeltip onaylamak icin) */
export function MockupAnalysisModal({ mockup, onClose, onSaved, vars, brandOptions }: {
  mockup: CustomMockup; onClose: () => void; onSaved: (m: CustomMockup) => void;
  /** Rexven (tedarikci) katalogu: marka -> model -> stoktaki renkler */
  vars?: Vars;
  /** Secilebilecek markalar (varsayilan + katalog + mockuplarda kullanilan) */
  brandOptions?: string[];
}) {
  const d = useAnalysisDraft(mockup, vars, brandOptions);
  const [busy, setBusy] = useState<"ai" | "save" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const imgUrl = mockup.imageUrl.startsWith("http") ? mockup.imageUrl : `${getBackendUrl()}${mockup.imageUrl}`;
  const areaCount = Array.isArray(mockup.designAreas) && mockup.designAreas.length ? mockup.designAreas.length : mockup.designAreaW > 20 ? 1 : 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const runAi = async () => {
    setBusy("ai"); setErr(null);
    try { d.fill(await call<MockupAnalysis>("POST", `/${mockup.id}/analyze`, { force: true })); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };
  const save = async (status: "draft" | "approved") => {
    setBusy("save"); setErr(null);
    try { onSaved(await d.save(status)); onClose(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Mockup analizi">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Mockup analizi</h2>
            <p className="text-xs text-gray-500">{mockup.label} · {areaCount} baskı alanı tanımlı{d.a ? ` · ${d.a.status === "approved" ? "onaylı" : "taslak"}` : " · analiz yok"}</p>
          </div>
          <button onClick={onClose} aria-label="Kapat" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid flex-1 gap-5 overflow-y-auto p-5 md:grid-cols-[240px_1fr]">
          <div className="space-y-3">
            <img src={imgUrl} alt={mockup.label} className="w-full rounded-lg border border-gray-200 bg-gray-50 object-contain" />
            <button onClick={runAi} disabled={busy !== null}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-indigo-600" />} {d.a ? "AI ile yeniden analiz et" : "AI ile analiz et"}
            </button>
            <p className="text-xs text-gray-500">Alanlarla birlikte kurmak için mockupun "Alanı düzenle" penceresindeki "AI ile otomatik kur" hem alanları hem analizi tek seferde yapar.</p>
          </div>
          <div>
            <MockupAnalysisFields d={d} areaCount={areaCount} />
            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={() => save("draft")} disabled={busy !== null} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50">Taslak kaydet</button>
          <button onClick={() => save("approved")} disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Onayla ve kaydet
          </button>
        </div>
      </div>
    </div>
  );
}
